import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fields", "message", "download", "submit", "reconnect"]
  static values = { statusUrl: String }

  connect() {
    this.connected = true
    if (this.statusUrlValue) {
      this.setBusy(true)
      this.poll()
    }
  }

  disconnect() {
    this.connected = false
    clearTimeout(this.timer)
    this.request?.abort()
  }

  async submit(event) {
    event.preventDefault()
    if (this.fieldsTarget.disabled) return
    const form = event.currentTarget
    const body = new FormData(form)
    this.setBusy(true)
    this.downloadTarget.hidden = true
    this.messageTarget.textContent = "Starting your export…"
    try {
      const result = await this.fetchJSON(form.action, { method: "POST", body })
      if (!this.connected) return
      this.statusUrlValue = result.status_url
      history.replaceState(history.state, "", result.page_url)
      this.receive(result)
    } catch (error) {
      this.showError(error)
    }
  }

  async poll() {
    try {
      const result = await this.fetchJSON(this.statusUrlValue)
      if (this.connected) this.receive(result)
    } catch (error) {
      this.showError(error)
    }
  }

  receive(result) {
    this.messageTarget.textContent = result.message
    this.showReconnect(result.reconnect_url)
    if (["queued", "processing"].includes(result.status)) {
      this.timer = setTimeout(() => this.poll(), 2000)
      return
    }
    this.statusUrlValue = ""
    this.setBusy(false)
    if (result.download_url) {
      this.downloadTarget.href = result.download_url
      this.downloadTarget.hidden = false
      this.downloadTarget.click()
    }
  }

  async fetchJSON(url, options = {}) {
    this.request = new AbortController()
    const response = await fetch(url, {
      ...options,
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      signal: this.request.signal,
      cache: "no-store"
    })
    if (response.redirected || !response.headers.get("content-type")?.includes("application/json")) {
      throw new Error("Please refresh this page and sign in again to continue.")
    }
    const result = await response.json()
    if (!response.ok) {
      this.showReconnect(result.reconnect_url)
      throw new Error(result.error || "Could not check your export. Refresh this page to try again.")
    }
    return result
  }

  showError(error) {
    if (!this.connected || error.name === "AbortError") return
    this.messageTarget.textContent = error instanceof TypeError ? "Connection interrupted. Refresh this page to check your export." : error.message
    this.setBusy(false)
  }

  showReconnect(url) {
    if (!this.hasReconnectTarget || !url) return
    this.reconnectTarget.href = url
    this.reconnectTarget.hidden = false
  }

  setBusy(busy) {
    this.fieldsTarget.disabled = busy
    this.submitTarget.value = busy ? "Preparing Excel…" : "Download Excel"
    this.element.setAttribute("aria-busy", busy)
  }
}
